import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import FormWithProvider from 'components/forms/form/Form';
import { FormContextParts } from 'components/forms/form/Form.types';
import useAppModal from 'hooks/useAppModal';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import { UpdateDbsSettingsPayload } from 'models/DbsSettings';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import useFetchDbsSettings, {
  DBS_SETTINGS_BASE_QUERY_KEY,
} from '../../../useFetchDbsSettings';
import useUpdateDbsSettingsMutation from '../../../useUpdateDbsSettingsMutation';
import {
  COLOR_FIELDS,
  ColorSettingsFormValues,
  getColorSettingsDefaultValues,
  getColorSettingsSchema,
  toApiColor,
} from './ColorSettings.schema';
import ColorSettingsModalContent from './ColorSettingsModalContent';

export const colorSettingsModalId = 'color-settings-modal';

const ColorSettingsModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { agencyUid } = useAppUser();
  const { closeModal } = useAppModal();
  const { notifyError, notifySuccess } = useNotify();
  const { dbsSettings } = useFetchDbsSettings();
  const { mutateAsync: updateDbsSettings } = useUpdateDbsSettingsMutation();
  const queryClient = useQueryClient();

  const handleClose = () => closeModal(colorSettingsModalId);

  const handleSubmit = async (
    formValues: ColorSettingsFormValues,
    _event: unknown,
    { formState: { dirtyFields } }: FormContextParts<ColorSettingsFormValues>,
  ) => {
    const payload: UpdateDbsSettingsPayload = {};

    COLOR_FIELDS.forEach((field) => {
      if (dirtyFields[field]) {
        payload[field] = formValues[field]
          ? toApiColor(formValues[field])
          : null;
      }
    });

    if (Object.keys(payload).length === 0) {
      handleClose();
      return;
    }

    try {
      await updateDbsSettings({ agencyUid: agencyUid!, payload });

      await queryClient.invalidateQueries([
        DBS_SETTINGS_BASE_QUERY_KEY,
        agencyUid,
      ]);

      notifySuccess(t('pageDirectBookingEngine.colorSettings.updateSuccess'));
      handleClose();
    } catch (error: any) {
      notifyError(
        getErrorMessage({
          ...error?.response?.data,
          baseKey: 'pageDirectBookingEngine.apiErrorCodes.',
          t,
        }),
      );
    }
  };

  return (
    <FormWithProvider
      defaultValues={getColorSettingsDefaultValues(dbsSettings)}
      dirtyChecker
      onSubmit={handleSubmit}
      schema={getColorSettingsSchema(t)}
    >
      <ColorSettingsModalContent onClose={onClose} />
    </FormWithProvider>
  );
};

export default ColorSettingsModal;
